import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { apiCallable } from '@/lib/api'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Plus, Bell, BellOff, RefreshCw, Trash2, Pencil } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { AddClassificationDialog } from '@/components/AddClassificationDialog'
import { EditClassificationDialog } from '@/components/EditClassificationDialog'
import type { Classification } from '@/types'

interface ClassificationsListProps {
  groupId: string
  canManageClassifications: boolean
}

export function ClassificationsList({ groupId, canManageClassifications }: ClassificationsListProps) {
  const [classifications, setClassifications] = useState<Classification[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [updatingIds, setUpdatingIds] = useState<Set<string>>(new Set())
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<Classification | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Classification | null>(null)
  const [deleting, setDeleting] = useState(false)
  const { t } = useTranslation()

  const loadClassifications = useCallback(async () => {
    if (!groupId) return

    setLoading(true)
    setError(null)

    try {
      const getClassificationsFn = apiCallable('getClassifications')
      const result = await getClassificationsFn({ groupId })
      const data = result.data as { success: boolean; classifications: Classification[] }

      if (data.success) {
        setClassifications(data.classifications)
      } else {
        setError(t('classifications.failedToLoad'))
      }
    } catch (err) {
      console.error('Failed to load classifications:', err)
      setError(t('classifications.failedToLoad'))
    } finally {
      setLoading(false)
    }
  }, [groupId])

  useEffect(() => {
    loadClassifications()
  }, [loadClassifications])

  const handleAlertToggle = async (classificationId: string, enabled: boolean) => {
    // Optimistic update
    setClassifications(prev =>
      prev.map(c =>
        c.id === classificationId ? { ...c, alertEnabled: enabled } : c
      )
    )

    setUpdatingIds(prev => new Set(prev).add(classificationId))

    try {
      const updateFn = apiCallable('updateClassification')
      await updateFn({
        groupId,
        classificationId,
        alertEnabled: enabled
      })
    } catch (err) {
      console.error('Failed to update classification:', err)
      // Revert on error
      setClassifications(prev =>
        prev.map(c =>
          c.id === classificationId ? { ...c, alertEnabled: !enabled } : c
        )
      )
    } finally {
      setUpdatingIds(prev => {
        const next = new Set(prev)
        next.delete(classificationId)
        return next
      })
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return

    setDeleting(true)

    try {
      const deleteClassificationFn = apiCallable('deleteClassification')
      await deleteClassificationFn({
        groupId,
        classificationId: deleteTarget.id,
      })
      setDeleteTarget(null)
      loadClassifications()
    } catch (err) {
      console.error('Failed to delete classification:', err)
      setError(t('classifications.failedToDelete'))
      setDeleteTarget(null)
    } finally {
      setDeleting(false)
    }
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <Alert variant="destructive" className="mb-4 text-left">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <Button variant="outline" onClick={loadClassifications}>
            <RefreshCw className="h-4 w-4 mr-2" />
            {t('common.retry')}
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-serif text-[28px] font-medium leading-[1.05] tracking-[-0.005em] text-ink">
            {t('classifications.title')}
          </h2>
          <hr className="gold-rule mt-3" />
          <p className="mt-3 text-[13px] leading-[1.55] text-ink-soft">
            {t('classifications.count', { count: classifications.length, defaultValue: '{{count}} classifications' })}
          </p>
        </div>
        <Button
          disabled={!canManageClassifications}
          onClick={() => setAddDialogOpen(true)}
        >
          <Plus className="h-4 w-4" strokeWidth={1.6} />
          {t('classifications.addClassification')}
        </Button>
      </div>

      {classifications.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            {t('classifications.noClassifications')}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="divide-y">
              {classifications.map((classification) => (
                <div
                  key={classification.id}
                  className="p-4 flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: classification.color }}
                    />
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{classification.displayName}</span>
                        {classification.isSystem && (
                          <Badge variant="outline" className="text-xs">{t('classifications.system')}</Badge>
                        )}
                      </div>
                      {classification.description && (
                        <p className="text-sm text-muted-foreground">
                          {classification.description}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      {classification.alertEnabled ? (
                        <Bell className="h-4 w-4 text-orange-500" />
                      ) : (
                        <BellOff className="h-4 w-4 text-ink-soft/70" />
                      )}
                      <Switch
                        checked={classification.alertEnabled}
                        onCheckedChange={(checked) => handleAlertToggle(classification.id, checked)}
                        disabled={updatingIds.has(classification.id)}
                      />
                      <span className="text-sm text-muted-foreground">
                        {classification.alertEnabled ? t('classifications.alertsOn') : t('classifications.alertsOff')}
                      </span>
                    </div>

                    {canManageClassifications && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-muted-foreground hover:text-foreground"
                        onClick={() => setEditTarget(classification)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    )}

                    {!classification.isSystem && canManageClassifications && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-muted-foreground hover:text-destructive"
                        onClick={() => setDeleteTarget(classification)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add Classification Dialog */}
      <AddClassificationDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        groupId={groupId}
        onClassificationCreated={loadClassifications}
      />

      {/* Edit Classification Dialog */}
      <EditClassificationDialog
        open={!!editTarget}
        onOpenChange={(open) => !open && setEditTarget(null)}
        groupId={groupId}
        classification={editTarget}
        onClassificationUpdated={loadClassifications}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('classifications.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('classifications.deleteConfirm', { name: deleteTarget?.displayName })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? t('common.deleting') : t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
